// VIDEO-EDITING — b-roll ripple-insert ops. Mirrors the inline editor
// today: insertBroll snapshots the next clip array + records a TimelineOp
// so the saved patch can replay the shift across every language. Heavy
// math (the spec mutation itself) stays in @wiser/remotion-layers — this
// slice is just the store-facing wrapper.
import type { StoreApi } from "zustand";

import {
  insertBrollAt,
  removeClipRipple,
  durationOfClip,
  durationOfClips,
} from "@wiser/remotion-layers";

import type { Clip } from "../../types";
import { applyOpToSegments } from "../../types/timeline";
import { EMPTY_DRAFT } from "../utils";
import type { VideoEditorState } from "../types";

type Set = StoreApi<VideoEditorState>["setState"];
type Get = StoreApi<VideoEditorState>["getState"];

export type ClipActions = Pick<VideoEditorState, "insertBroll" | "removeClipAt">;

function newClipId(): string {
  const rand =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  return `clip-${rand}`.slice(0, 64);
}

export function createClipActions(set: Set, get: Get): ClipActions {
  const buildSpec = (projectId: string) => {
    const state = get();
    const project = state.project;
    if (!project || project.id !== projectId) return null;
    const draft = state.drafts[projectId] ?? EMPTY_DRAFT;
    const clips: Clip[] = draft.draftClips ?? project.clips ?? [];
    return { project, draft, clips };
  };

  return {
    insertBroll: (projectId, atSec, broll) => {
      const ctx = buildSpec(projectId);
      if (!ctx) return;
      const totalDurationSec = durationOfClips(ctx.clips) || 0;
      const spec = {
        projectId,
        source: null,
        clips: ctx.clips,
        captions: {
          segments: ctx.draft.draftSegments ?? [],
          language: "und",
        },
        overlays: ctx.draft.draftOverlays ?? ctx.project.overlays ?? [],
        dimensions: { width: 1080, height: 1920 },
        durationSec: totalDurationSec,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any;
      const next = insertBrollAt(spec, atSec, broll, newClipId);
      const delta = durationOfClip(broll);
      const op = { kind: "insert" as const, atSec, delta };
      set((state) => {
        const draft = state.drafts[projectId] ?? EMPTY_DRAFT;
        return {
          ...state,
          drafts: {
            ...state.drafts,
            [projectId]: {
              ...draft,
              draftClips: next.clips,
              draftOverlays: next.overlays as Clip[] extends never
                ? never
                : typeof draft.draftOverlays,
              draftSegments:
                draft.draftSegments != null
                  ? applyOpToSegments(draft.draftSegments, op)
                  : draft.draftSegments,
              timelineOps: [...draft.timelineOps, op],
              lastEditedAt: Date.now(),
            },
          },
        };
      });
    },
    removeClipAt: (projectId, clipIndex) => {
      const ctx = buildSpec(projectId);
      if (!ctx) return;
      const targetClip = ctx.clips[clipIndex];
      if (!targetClip) return;
      const totalDurationSec = durationOfClips(ctx.clips) || 0;
      const spec = {
        projectId,
        source: null,
        clips: ctx.clips,
        captions: {
          segments: ctx.draft.draftSegments ?? [],
          language: "und",
        },
        overlays: ctx.draft.draftOverlays ?? ctx.project.overlays ?? [],
        dimensions: { width: 1080, height: 1920 },
        durationSec: totalDurationSec,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any;
      const next = removeClipRipple(spec, targetClip.id);
      if (!next) return;
      const offsetSec = ctx.clips
        .slice(0, clipIndex)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .reduce((acc: number, c: any) => acc + durationOfClip(c), 0);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const removedDur = durationOfClip(targetClip as any);
      const op = {
        kind: "remove" as const,
        r0: offsetSec,
        r1: offsetSec + removedDur,
      };
      set((state) => {
        const draft = state.drafts[projectId] ?? EMPTY_DRAFT;
        return {
          ...state,
          drafts: {
            ...state.drafts,
            [projectId]: {
              ...draft,
              draftClips: next.clips,
              draftSegments:
                draft.draftSegments != null
                  ? applyOpToSegments(draft.draftSegments, op)
                  : draft.draftSegments,
              timelineOps: [...draft.timelineOps, op],
              lastEditedAt: Date.now(),
            },
          },
        };
      });
    },
  };
}
